import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Loader2 } from 'lucide-react'
import { useImageSlots, useSlotGenerate } from '@/hooks/useImageSlots'
import { useAuditModelConfig } from '@/hooks/useAuditModelConfig'
import { useAuditImageDefaults } from '@/hooks/useAuditImageDefaults'
import { ImageSlotPanel } from './ImageSlotPanel'

interface ImageSlotsSectionProps {
  remakeId: string
}

export function ImageSlotsSection({ remakeId }: ImageSlotsSectionProps) {
  const { t } = useTranslation()
  const { data: slots, isLoading } = useImageSlots(remakeId)
  const generateMutation = useSlotGenerate()
  const modelConfig = useAuditModelConfig()
  const { defaults: imageDefaults } = useAuditImageDefaults()
  const [generatingIds, setGeneratingIds] = useState<Set<string>>(new Set())

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 p-4 text-neutral-500">
        <Loader2 className="w-4 h-4 animate-spin" />
        <span className="text-sm">{t('amazonListing.slots.generating')}</span>
      </div>
    )
  }

  if (!slots || slots.length === 0) return null

  const handleGenerate = (slotId: string) => {
    setGeneratingIds((prev) => new Set(prev).add(slotId))
    generateMutation.mutate(slotId, {
      onSettled: () => {
        setGeneratingIds((prev) => {
          const next = new Set(prev)
          next.delete(slotId)
          return next
        })
      },
    })
  }

  return (
    <div className="space-y-6">
      {slots.map((slot) => (
        <ImageSlotPanel
          key={slot.id}
          slot={slot}
          onGenerate={handleGenerate}
          isGenerating={generatingIds.has(slot.id)}
          modelConfig={modelConfig}
          imageDefaults={imageDefaults}
        />
      ))}
    </div>
  )
}
